import React, { useContext, useEffect, useReducer } from 'react';
import { changeGamePositionReducer } from './changeGamePositionReducer';
import { ChangeGamePositionActionTypes } from './changeGamePositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangeGamePositionContext,
	changeGamePositionInitialState,
	TChangeGamePositionSelectedItem,
	TTypeOfChangeGamePositionModalWindow,
} from './changeGamePositionContext';
import { IItemForGetShortInfo, IGamePositionItem, IGetGamePositionList } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getGamePositionList } from 'backend/games';

const ChangeGamePositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changeGamePositionReducer, changeGamePositionInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<IGetGamePositionList, string>(
		state.IDSelectedRestaurant ? getGamePositionList : null,
		state.IDSelectedRestaurant || '',
	);

	const setIDCustomer = (value: TChangeGamePositionSelectedItem) => {
		dispatch({
			type: ChangeGamePositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setIDRestaurant = (value: TChangeGamePositionSelectedItem) => {
		dispatch({
			type: ChangeGamePositionActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ChangeGamePositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: IGamePositionItem[]) => {
		dispatch({
			type: ChangeGamePositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const reorderItems = (value: IGamePositionItem[]) => {
		dispatch({
			type: ChangeGamePositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangeGamePositionModalWindow) => {
		dispatch({
			type: ChangeGamePositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: ChangeGamePositionActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisibleAdditionalRestaurantsModal: value,
				isApplyToOne: value ? false : state.isApplyToOne,
				selectedAdditionalRestaurants: value ? [] : state.selectedAdditionalRestaurants,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: ChangeGamePositionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: string[]) => {
		dispatch({
			type: ChangeGamePositionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				selectedAdditionalRestaurants: value,
			},
		});
	};

	const setFetchedRestaurants = (value: IItemForGetShortInfo[]) => {
		dispatch({
			type: ChangeGamePositionActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	};

	useEffect(() => {
		response &&
			dispatch({
				type: ChangeGamePositionActionTypes.SET_POSITIONS_DATA,
				payload: {
					positionsData: response?.data.games,
				},
			});
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.IDSelectedRestaurant]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	return (
		<ChangeGamePositionContext.Provider
			value={{
				setIDCustomer,
				setIDRestaurant,
				resetSelectedControls,
				setPositionsData,
				reorderItems,
				changeTypeOfModalWindow,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				setFetchedRestaurants,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				positionsData: state.positionsData,
				typeOfModalWindow: state.typeOfModalWindow,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				fetchedRestaurants: state.fetchedRestaurants,
			}}>
			{children}
		</ChangeGamePositionContext.Provider>
	);
};

export default ChangeGamePositionState;
